import React, { useState } from 'react';
import {
    Button,
    IconButton,
    Card,
    Typography,
    Alert,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from '@material-tailwind/react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import { rootUrl } from '../../../../config/config';
import ChargesDialog from '../components/ChargesDialog';

export default function JobCardChargesTab({
    jobCardId,
    charges = [],
    chargeTypes = [],
    locked = false,
    onChargeAdded,
    onChargeUpdated,
    onChargeRemoved
}) {
    const { user } = useAuth();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCharge, setSelectedCharge] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [chargeToDelete, setChargeToDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleAddCharge = () => {
        setSelectedCharge(null);
        setDialogOpen(true);
    };

    const handleEditCharge = (charge) => {
        if (!locked && !charge.locked) {
            setSelectedCharge(charge);
            setDialogOpen(true);
        }
    };

    const handleDeleteClick = (charge) => {
        if (!locked && !charge.locked) {
            setChargeToDelete(charge);
            setDeleteDialogOpen(true);
        }
    };

    const handleConfirmDelete = async () => {
        if (!chargeToDelete) return;

        try {
            setLoading(true);
            setError(null);

            const response = await axios.delete(
                `${rootUrl}/api/job-cards/${jobCardId}/charges/${chargeToDelete.id}`,
                {
                    headers: { Authorization: `Bearer ${user?.token}` }
                }
            );

            if (response.data.success) {
                setDeleteDialogOpen(false);
                setChargeToDelete(null);
                onChargeRemoved();
            } else {
                setError(response.data.message || 'Failed to delete charge');
            }
        } catch (err) {
            console.error('Error deleting charge:', err);
            setError(err.response?.data?.message || 'Failed to delete charge');
        } finally {
            setLoading(false);
        }
    };

    const getChargeTypeName = (typeId) => {
        const type = chargeTypes.find(t => t.id === typeId);
        return type?.name || 'Unknown';
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const totalCharges = charges.reduce((sum, c) => sum + parseFloat(c.total_amount || 0), 0);

    return (
        <div className="py-6">
            {error && <Alert color="red" className="mb-4">{error}</Alert>}

            {locked && (
                <Alert color="yellow" className="mb-4">
                    This job card is completed/closed. Charges are locked and cannot be modified.
                </Alert>
            )}

            {/* Add Button */}
            <div className="mb-4">
                <Button
                    color="blue"
                    onClick={handleAddCharge}
                    disabled={locked}
                >
                    + Add Charge
                </Button>
            </div>

            {/* Charges Table */}
            {charges.length > 0 ? (
                <Card className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-4 text-sm font-semibold text-gray-900">Type</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Description</th>
                                <th className="p-4 text-sm font-semibold text-gray-900 text-right">Amount</th>
                                <th className="p-4 text-sm font-semibold text-gray-900 text-right">Discount</th>
                                <th className="p-4 text-sm font-semibold text-gray-900 text-right">Total</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Paid By</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Week</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Status</th>
                                <th className="p-4 text-sm font-semibold text-gray-900">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {charges.map((charge, index) => (
                                <tr key={charge.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="p-4 text-sm text-gray-900 font-semibold">
                                        {getChargeTypeName(charge.charge_type_id)}
                                    </td>
                                    <td className="p-4 text-sm text-gray-700">
                                        {charge.description || '-'}
                                    </td>
                                    <td className="p-4 text-sm text-gray-900 font-semibold text-right">
                                        {formatCurrency(charge.amount)}
                                    </td>
                                    <td className="p-4 text-sm text-gray-900 text-right">
                                        {formatCurrency(charge.discount_amount)}
                                    </td>
                                    <td className="p-4 text-sm text-gray-900 font-bold text-right text-blue-600">
                                        {formatCurrency(charge.total_amount)}
                                    </td>
                                    <td className="p-4 text-sm text-gray-700">
                                        {charge.paid_by || '-'}
                                    </td>
                                    <td className="p-4 text-sm text-gray-700">
                                        {charge.week_key || '-'}
                                    </td>
                                    <td className="p-4 text-sm">
                                        {charge.locked ? (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">Closed</span>
                                        ) : (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Open</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-sm">
                                        <div className="flex gap-2">
                                            {!charge.locked && !locked && (
                                                <>
                                                    <IconButton
                                                        size="sm"
                                                        variant="text"
                                                        color="blue"
                                                        onClick={() => handleEditCharge(charge)}
                                                    >
                                                        <PencilIcon className="w-4 h-4" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="sm"
                                                        variant="text"
                                                        color="red"
                                                        onClick={() => handleDeleteClick(charge)}
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                    </IconButton>
                                                </>
                                            )}
                                            {(charge.locked || locked) && (
                                                <Typography className="text-xs text-gray-500">-</Typography>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr className="bg-blue-50 font-bold border-t-2 border-blue-500">
                                <td colSpan="4" className="p-4 text-right text-gray-900">
                                    Total Charges:
                                </td>
                                <td className="p-4 text-right text-blue-600">
                                    {formatCurrency(totalCharges)}
                                </td>
                                <td colSpan="4"></td>
                            </tr>
                        </tbody>
                    </table>
                </Card>
            ) : (
                <Card className="p-8 text-center">
                    <Typography className="text-gray-500">
                        No charges added yet
                    </Typography>
                </Card>
            )}

            {/* Charges Dialog */}
            <ChargesDialog
                open={dialogOpen}
                charge={selectedCharge}
                jobCardId={jobCardId}
                chargeTypes={chargeTypes}
                locked={locked}
                onSave={() => {
                    selectedCharge ? onChargeUpdated() : onChargeAdded();
                }}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectedCharge(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} handler={setDeleteDialogOpen}>
                <DialogHeader>Confirm Delete</DialogHeader>
                <DialogBody>
                    Are you sure you want to delete this charge? This action cannot be undone.
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        color="gray"
                        onClick={() => setDeleteDialogOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        onClick={handleConfirmDelete}
                        loading={loading}
                        disabled={loading}
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}
